import { CardContent, DialogActions, DialogContent, DialogContentText, Tab } from '@mui/material';
import { useConfig } from '@revenuewell/logic-config';
import { useNotification } from '@revenuewell/logic-notification';
import { useOidcWithAdmin } from '@revenuewell/logic-oidc';
import { PrimaryButton, SecondaryButton } from 'apps/wrapper/src/components/shared/buttons';
import { Dialog, DialogTitle } from 'apps/wrapper/src/components/shared/dialog/dialog.styles';
import { usePatientProfile } from 'apps/wrapper/src/hooks/use-patient-profile';
import {
  ContactApi,
  PatientLanguage,
  PatientPreferencesRequest,
  PatientPreferencesResponse
} from 'apps/wrapper/src/services/api-client/contact-api';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { getCustomizedEmailCount } from 'apps/wrapper/src/components/layout/slideout/patient-profile-widget/views/comm-preferences/utils/get-customized-email-count';
import { getCustomizedTextCount } from 'apps/wrapper/src/components/layout/slideout/patient-profile-widget/views/comm-preferences/utils/get-customized-text-count';
import { getCustomizedDMCount } from 'apps/wrapper/src/components/layout/slideout/patient-profile-widget/views/comm-preferences/utils/get-customized-dm-count';
import { getCustomizedPhoneCount } from 'apps/wrapper/src/components/layout/slideout/patient-profile-widget/views/comm-preferences/utils/get-customized-phone-count';
import { Card, CardTitle } from '../../patient-profile-page.styles';
import { CommPreferencesGeneralView } from './comm-preferences-general';
import { Container, SaveContainer, TabContainer, TabContent, Tabs, TabTitle } from './comm-preferences.styles';
import { CommPreferencesEmailView } from './comm-preferences-email';
import { CommPreferencesTextView } from './comm-preferences-text';
import { CommPreferencesDirectEmailView } from './comm-preferences-direct-email';
import { CommPreferencesPhoneView } from './comm-preferences-phone';

export const CommPreferencesView: FC = () => {
  const { patientPreferences, contactDetails, patientInfo, refreshPatientPreferences } = usePatientProfile();
  const [selectedTab, setSelectedTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  type ChangeKeys = 'updateFamilyOptions' | 'updateLanguageOptions' | 'updateLocalPreferences';
  const [changes, setChanges] = useState<Partial<Record<ChangeKeys, any>>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [targetSelectedBeforeUnload, setTargetSelectedBeforeUnload] = useState<HTMLElement | null>(null);
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const contactApi = useMemo(() => new ContactApi(config, oidcService), [config, oidcService]);
  const { showSuccess } = useNotification();

  const actions: Record<ChangeKeys, (value: any) => Promise<void>> = {
    updateFamilyOptions: async (familyMemberId: number) => {
      await contactApi.patchPatientPreferences(patientInfo.id, {
        ...patientPreferences,
        familyCommunicationReceiverId: familyMemberId
      });
    },
    updateLanguageOptions: async (language: PatientLanguage) => {
      await contactApi.patchPatientPreferences(patientInfo.id, {
        ...patientPreferences,
        language
      });
    },
    updateLocalPreferences: async (_localPreferences: PatientPreferencesResponse) => {
      const localPreferences = { ...patientPreferences, ..._localPreferences };

      await contactApi.patchPatientPreferences(
        localPreferences.patientId,
        localPreferences as PatientPreferencesRequest
      );
    }
  };

  const handleSave = async () => {
    setIsLoading(true);

    try {
      await Promise.all(Object.entries(changes).map(([key, value]) => actions[key as ChangeKeys](value)));
      await refreshPatientPreferences();
      showSuccess('Preferences successfully saved');
      setIsDirty(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = ''; // Shows a default confirmation dialog from the browser
      }
    },
    [isDirty]
  );

  const handleLinkClick = useCallback(
    (event: any) => {
      const target = event.target.closest('a') || event.target.closest('.patient-profile-navbar-item');

      if (target && isDirty && target !== targetSelectedBeforeUnload) {
        if (target.getAttribute('target') === '_blank') {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        setTargetSelectedBeforeUnload(target);
      }
    },
    [isDirty, targetSelectedBeforeUnload]
  );

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('click', handleLinkClick, true);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [handleBeforeUnload, handleLinkClick]);

  const setChange = (key: string, value: any) => {
    setIsDirty(true);
    setChanges({ ...changes, [key]: value });
  };

  const onChangeTab = (event: React.ChangeEvent<object>, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <>
      <Container>
        <Card>
          <CardContent>
            <CardTitle className="hr">{contactDetails.firstName}’s Preferences</CardTitle>
            <TabContainer>
              <Tabs orientation="vertical" variant="scrollable" value={selectedTab} onChange={onChangeTab}>
                <Tab label={<TabTitle>General</TabTitle>} />
                <Tab
                  label={
                    <TabTitle>
                      Email
                      <span>{getCustomizedEmailCount(patientPreferences)} of 9 active</span>
                    </TabTitle>
                  }
                />
                <Tab
                  label={
                    <TabTitle>
                      Text
                      <span>{getCustomizedTextCount(patientPreferences)} of 6 active</span>
                    </TabTitle>
                  }
                />
                <Tab
                  label={
                    <TabTitle>
                      Direct Mail
                      <span>{getCustomizedDMCount(patientPreferences)} of 7 active</span>
                    </TabTitle>
                  }
                />
                <Tab
                  label={
                    <TabTitle>
                      Phone
                      <span>{getCustomizedPhoneCount(patientPreferences)} of 1 active</span>
                    </TabTitle>
                  }
                />
              </Tabs>
              <TabContent>
                {selectedTab === 0 && <CommPreferencesGeneralView setChange={setChange} changes={changes} />}
                {selectedTab === 1 && <CommPreferencesEmailView setChange={setChange} changes={changes} />}
                {selectedTab === 2 && <CommPreferencesTextView setChange={setChange} changes={changes} />}
                {selectedTab === 3 && <CommPreferencesDirectEmailView setChange={setChange} changes={changes} />}
                {selectedTab === 4 && <CommPreferencesPhoneView setChange={setChange} changes={changes} />}
              </TabContent>
            </TabContainer>
          </CardContent>
        </Card>
        <SaveContainer>
          <PrimaryButton loading={isLoading} disabled={!isDirty} onClick={handleSave}>
            Save
          </PrimaryButton>
        </SaveContainer>
      </Container>
      <Dialog open={!!targetSelectedBeforeUnload} onClose={() => setTargetSelectedBeforeUnload(null)}>
        <DialogContent>
          <DialogTitle>Are you sure you want to leave?</DialogTitle>
          <DialogContentText sx={{ marginTop: '16px' }}>
            You have unsaved changes, are you sure you want to navigate away?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setTargetSelectedBeforeUnload(null)}>
            STAY AND CONTINUE EDITING
          </SecondaryButton>
          <PrimaryButton
            onClick={() => {
              targetSelectedBeforeUnload?.click();
              setTargetSelectedBeforeUnload(null);
            }}
          >
            NAVIGATE AWAY
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
